import apiServices from '@/modules/data-layer/services/ApiServices';

// Simple in-memory cache
const memoryCache = {};
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function resolveDatasourceName(datasourceId, datasourceMap = {}) {
  if (datasourceId == null || datasourceId === '') return null;
  const key = String(datasourceId);
  if (datasourceMap[key]) return datasourceMap[key];
  if (datasourceMap[datasourceId]) return datasourceMap[datasourceId];
  return `Datasource ${datasourceId}`;
}

function rowDatasourceId(row) {
  if (!row) return null;
  return row.datasource ?? row.datasource_id ?? row.datasourceId ?? null;
}

function applyMaps(items, indicatorMap = {}, datasourceMap = {}) {
  return items.map((item) => ({
    ...item,
    indicatorName: indicatorMap[item.indicatorId] || item.indicatorName,
    datasourceName: resolveDatasourceName(item.datasourceId, datasourceMap),
  }));
}

/**
 * Pick current/previous points for comparison.
 * - If a datasource is selected: prefer that source for current + previous;
 *   if it has no previous period, fall back to previous available period (any source).
 * - If none is selected: prefer previous from the same datasource as the latest row;
 *   otherwise fall back to the next period.
 */
function pickComparisonPoints(rows, preferredDatasourceId = null) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const preferredId = preferredDatasourceId == null || preferredDatasourceId === ''
    ? null
    : String(preferredDatasourceId);

  let currentData = rows[0];
  let previousPoint = null;
  let usedPreferredDatasource = false;

  if (preferredId) {
    const preferredRows = rows.filter((row) => String(rowDatasourceId(row)) === preferredId);
    if (preferredRows.length >= 1) {
      currentData = preferredRows[0];
      usedPreferredDatasource = true;
      if (preferredRows.length >= 2) {
        previousPoint = preferredRows[1];
      }
    }
  } else {
    // No selected datasource: keep same-datasource-as-latest preference
    const currentDsId = rowDatasourceId(currentData);
    if (currentDsId != null) {
      previousPoint = rows.find(
        (row, idx) => idx > 0 && String(rowDatasourceId(row)) === String(currentDsId),
      ) || null;
    }
  }

  // Fallback: previous period from any remaining newer-to-older row after current
  if (!previousPoint) {
    const currentIdx = rows.indexOf(currentData);
    if (currentIdx >= 0) {
      previousPoint = rows.find((row, idx) => idx > currentIdx) || null;
    } else {
      previousPoint = rows[1] || null;
    }
  }

  if (!currentData || !previousPoint) return null;

  return {
    currentData,
    previousPoint,
    currentDatasourceId: rowDatasourceId(currentData),
    usedPreferredDatasource,
    previousFromPreferredDatasource: preferredId
      ? String(rowDatasourceId(previousPoint)) === preferredId
      : String(rowDatasourceId(previousPoint)) === String(rowDatasourceId(currentData)),
  };
}

export default {
  /**
   * Fetches change data for a list of indicators
   * @param {Array} indicators List of indicator IDs
   * @param {String} dashboardName Name of the dashboard (used as cache key)
   * @param {Object} indicatorMap Map of indicatorId -> display name
   * @param {Number|String} locationId Active location id used for comparison
   * @param {Object} datasourceMap Map of datasourceId -> display name
   * @param {Number|String|null} preferredDatasourceId Selected control-panel datasource
   * @returns {Promise<Array>} Sorted list of top indicator changes
   */
  async getIndicatorChanges(
    indicators,
    dashboardName,
    indicatorMap = {},
    locationId = 1,
    datasourceMap = {},
    preferredDatasourceId = null,
  ) {
    if (!indicators || indicators.length === 0) return [];

    const dsKey = preferredDatasourceId == null || preferredDatasourceId === ''
      ? 'any'
      : preferredDatasourceId;

    // Check cache
    const cacheKey = `indicator_changes_v11_${dashboardName}_loc_${locationId}_ds_${dsKey}`;
    const cachedData = memoryCache[cacheKey];
    if (cachedData && (Date.now() - cachedData.timestamp < CACHE_TTL_MS)) {
      return applyMaps(cachedData.data, indicatorMap, datasourceMap);
    }

    try {
      const results = [];

      // Increase sample size to 25 for better variety across dashboards
      const indicatorsToCheck = indicators.slice(0, 25);

      // Fetch data for each indicator
      const promises = indicatorsToCheck.map(async (indicatorId) => {
        try {
          // Fetch recent periods for selected location (enough history for same-datasource pairing)
          const dataRes = await apiServices.getRequiredEndpoint(
            `data/?indicator=${indicatorId}&location=${locationId}&ordering=-period&size=20`,
          );

          const rows = dataRes?.data?.results;
          const comparison = pickComparisonPoints(rows, preferredDatasourceId);
          if (!comparison) return;

          const {
            currentData,
            previousPoint,
            currentDatasourceId,
          } = comparison;

          const currentValue = parseFloat(currentData.value);
          const previousValue = parseFloat(previousPoint.value);

          // Allow 0 values but prevent division by zero
          if (!Number.isNaN(currentValue) && !Number.isNaN(previousValue) && previousValue !== 0) {
            const changeRaw = ((currentValue - previousValue) / previousValue) * 100;
            const changePct = parseFloat(changeRaw.toFixed(1));

            let parsedDirection = 'none';
            if (changePct > 0) {
              parsedDirection = 'increase';
            } else if (changePct < 0) {
              parsedDirection = 'decrease';
            }

            results.push({
              indicatorId,
              indicatorName: indicatorMap[indicatorId] || `Indicator ${indicatorId}`,
              datasourceId: currentDatasourceId,
              datasourceName: resolveDatasourceName(currentDatasourceId, datasourceMap),
              currentValue: parseFloat(currentValue.toFixed(1)),
              previousValue: parseFloat(previousValue.toFixed(1)),
              currentPeriod: currentData.period,
              previousPeriod: previousPoint.period,
              change: changePct,
              direction: parsedDirection,
              magnitude: Math.abs(changePct),
              usedPreferredDatasource: comparison.usedPreferredDatasource,
              previousFromPreferredDatasource: comparison.previousFromPreferredDatasource,
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch change data for indicator ${indicatorId}`, err);
        }
      });

      await Promise.allSettled(promises);

      // Sort by absolute magnitude of change, descending
      results.sort((a, b) => b.magnitude - a.magnitude);

      // Keep top 8 most significant changes (increased from 5)
      const topChanges = results.slice(0, 8);

      // Save to cache
      memoryCache[cacheKey] = {
        timestamp: Date.now(),
        data: topChanges,
      };

      return applyMaps(topChanges, indicatorMap, datasourceMap);
    } catch (error) {
      console.error('Error fetching indicator changes:', error);
      return [];
    }
  },

  /**
   * Clears the cache for all dashboards or a specific one
   * @param {String} dashboardName Optional dashboard name to clear
   */
  clearCache(dashboardName = null) {
    if (dashboardName) {
      Object.keys(memoryCache).forEach((key) => {
        if (key.includes(`_${dashboardName}_`)) {
          delete memoryCache[key];
        }
      });
    } else {
      Object.keys(memoryCache).forEach((key) => {
        if (key.startsWith('indicator_changes_')) {
          delete memoryCache[key];
        }
      });
    }
  },
};
